<div class="topbar">
  <div class="topbar-content">
    <!-- Brand/Logo Section -->
    <div class="topbar-brand">
      <!-- Clickable (with its chevron cue) from the FIRST brand — the menu carries the
           membership list AND the create-a-brand road. A div needs the button role plus
           explicit key handlers to be keyboard-operable; both are gated on the same
           clickable condition so a menu-less selector stays out of the tab order. -->
      <div class="brand-selector focus-ring"
           [class.clickable]="brands().length > 0"
           [attr.role]="brands().length > 0 ? 'button' : null"
           [attr.tabindex]="brands().length > 0 ? 0 : null"
           [attr.aria-haspopup]="brands().length > 0 ? 'menu' : null"
           (click)="showBrandMenu($event)"
           (keydown.enter)="showBrandMenu($event)"
           (keydown.space)="showBrandMenu($event); $event.preventDefault()">
        <!-- Dynamic Logo -->
        @if (showLogo() && logoUrl()) {
        <img [src]="logoUrl()" [alt]="brandDisplayName()" class="brand-logo-img" />
        }
        @if (showBrandName()) {
        <span class="brand-name-text">{{ brandDisplayName() }}</span>
        }
        @if (!showLogo() && !showBrandName()) {
        <span class="brand-name-text">{{ brandDisplayName() }}</span>
        }
        @if (brands().length > 0) {
        <fa-duotone-icon [icon]="faChevronDown" class="brand-chevron"></fa-duotone-icon>
        }
      </div>

      <!-- Brand Menu -->
      <p-tieredMenu #brandMenu
                    [model]="brandMenuItems"
                    [popup]="true"
                    appendTo="body"
                    styleClass="brand-dropdown-menu">
        <ng-template pTemplate="item" let-item>
          <a class="p-menuitem-link">
            @if (item.data?.labelKey) {
            <!-- Action item (create a brand): leading icon + template-piped label, so the
                 text follows an interface-language switch. -->
            <div class="brand-menu-item brand-menu-action">
              <fa-duotone-icon [icon]="item.data.actionIcon" class="menu-icon"></fa-duotone-icon>
              <span class="brand-menu-name">{{ item.data.labelKey | translate }}</span>
            </div>
            } @else {
            <div class="brand-menu-item">
              <span class="brand-menu-name">{{ item.label }}</span>
            </div>
            }
          </a>
        </ng-template>
      </p-tieredMenu>
    </div>

    <!-- Search Section (centered). Search is NOW-only — disabled in "Time travel";
         the tooltip rides the wrapper, so the reason shows whether or not the control itself is
         disabled (a tooltip on the control would depend on the disabled state's own pointer behaviour). -->
    @if (searchAvailable()) {
    <div class="topbar-search"
         [pTooltip]="temporal.active() ? ('temporal.nav.todayOnly' | translate) : ''"
         tooltipPosition="bottom">
      <button type="button"
              class="search-trigger"
              [appTourTarget]="'search-trigger'"
              [disabled]="temporal.active()"
              (click)="openSearch()"
              [attr.aria-label]="'common.search' | translate">
        <fa-duotone-icon [icon]="faMagnifyingGlass" class="search-icon"></fa-duotone-icon>
        <span class="search-label">{{ 'common.search' | translate }}</span>
        @for (key of searchShortcutKeys(); track key; let last = $last) {
          <span class="search-shortcut">{{ key }}</span>
          @if (!last) { <span class="search-shortcut-sep">·</span> }
        }
      </button>
    </div>
    } @else {
    <div class="topbar-search"></div>
    }

    <!-- Right Section -->
    <div class="topbar-right">
      <div class="topbar-actions">
        <!-- STATUS PILLS LEAD THE STRIP, ACTIONS FOLLOW (Robert's ruling of 2026-09-29): the strip is
             right-aligned, so a pill that appears at its START grows the strip leftward and every button
             keeps its place — one in the middle pushed every button to its left when it came and went,
             a transient state moving the very buttons a member was aiming at. Both pills sit BEFORE the
             members-only block below, so a membership-less root (who never enters that block) sees them. -->

        <!-- Monitoring chip — the only visible marker of a platform root browsing WITHOUT a
             membership. Keyed on the synthetic marker ONLY — members with the root claim get no indicator. -->
        @if (access.isSyntheticRoot()) {
          <app-status-badge class="monitoring-chip" shape="pill" tone="warning" [icon]="faShieldAlt"
                            [label]="'topbar.monitoringMode' | translate"
                            [title]="'topbar.monitoringModeTitle' | translate"></app-status-badge>
        }

        <!-- Reconnecting chip — the live-updates-paused state: the socket has been down past its grace while a
             stream wants it, with the server reachable (ConnectivityService.reconnecting, the one home — under
             the offline or server-unreachable bar the bar speaks for the outage and the pill stands down,
             Robert's ruling of 2026-09-29). A quiet pill, never the full-width bar the two hard states earn
             (Robert's pick of 2026-09-29): everything still works, only others' changes stop arriving live.
             The full sentence rides the pill's title for a sighted member and a PERSISTENT status region for a
             screen reader — the pill is inserted with its content, which a live region does not announce
             reliably, so the region stays in the DOM and its text moves with the state (the globe's hidden-twin
             idiom, further down the strip). -->
        <span class="reconnecting-status sr-only" role="status">{{ connectivity.reconnecting() ? ('connectivity.liveUpdatesPaused' | translate) : '' }}</span>
        @if (connectivity.reconnecting()) {
          <app-status-badge class="reconnecting-chip" shape="pill" tone="info" [icon]="faArrowsRotate" [spinning]="true"
                            [label]="'connectivity.reconnecting' | translate"
                            [title]="'connectivity.liveUpdatesPaused' | translate"></app-status-badge>
        }

        @if (isMember()) {
        <!-- Feedback -->
        <button pButton type="button"
                class="p-button-text p-button-rounded topbar-button"
                [appTourTarget]="'topbar-feedback'"
                (click)="feedbackPanel.toggle()"
                [icon]="'pi pi-false'"
                [pTooltip]="'topbar.feedback' | translate"
                tooltipPosition="bottom"
                [showDelay]="500">
          <fa-duotone-icon [icon]="faCommentDots"></fa-duotone-icon>
        </button>

        <!-- Uploads (hidden when queue is empty; a write flow — suppressed in "Time travel") -->
        @if (hasActiveUploads() && !temporal.active()) {
        <div class="upload-button-wrapper">
          <button pButton type="button"
                  class="p-button-text p-button-rounded topbar-button"
                  (click)="uploadsPanel.toggle()"
                  [pTooltip]="'topbar.uploads' | translate"
                  tooltipPosition="bottom"
                  [showDelay]="500"
                  [icon]="'pi pi-false'">
            <fa-duotone-icon [icon]="faCloudArrowUp"></fa-duotone-icon>
          </button>
          @if (activeUploadsCount() > 0) {
          <span class="upload-badge active">{{ formatCount(activeUploadsCount()) }}</span>
          }
          @if (alertUploadsCount() > 0) {
          <span class="upload-badge alert">{{ formatCount(alertUploadsCount()) }}</span>
          }
        </div>
        }

        <!-- Notifications -->
        <div class="notification-wrapper">
          <button pButton type="button"
                  class="p-button-text p-button-rounded topbar-button"
                  (click)="openNotificationsPanel()"
                  [icon]="'pi pi-false'"
                  [pTooltip]="'topbar.notifications' | translate"
                  tooltipPosition="bottom"
                  [showDelay]="500">
            <fa-duotone-icon [icon]="faBell"></fa-duotone-icon>
          </button>
          @if (notificationCount() > 0) {
          <span class="notification-badge">{{ formatCount(notificationCount()) }}</span>
          }
        </div>

        <!-- Bookmarks. The enclosing isMember() block is the LOAD-BEARING gate for a
             membership-less monitoring root (they never enter it); this wrap acts only in a
             member-root's access-reload window — defense-in-depth, keep both. -->
        @if (!access.isReadOnlyRoot()) {
        <button pButton type="button"
                class="p-button-text p-button-rounded topbar-button"
                (click)="openBookmarksPanel()"
                [icon]="'pi pi-false'"
                [pTooltip]="'bookmarks.title' | translate"
                tooltipPosition="bottom"
                [showDelay]="500">
          <fa-duotone-icon [icon]="faBookmark"></fa-duotone-icon>
        </button>
        }

        <!-- Time travel — trigger faClockRotateLeft (Robert 2026-07-14, superseding the earlier
             faCalendarClock pick: the reversed clock IS the mode's own family glyph — the boundary
             interstitial + hollow states already use it; the seeming glyph collision is a LABELED
             profile-menu item + a labeled palette section header, so no ambiguity);
             active state = non-textual dot, the banner owns the instant -->
        <div class="time-travel-button-wrapper">
          <button pButton type="button"
                  class="p-button-text p-button-rounded topbar-button"
                  (click)="openTimeTravelPanel()"
                  [icon]="'pi pi-false'"
                  [pTooltip]="'temporal.panel.title' | translate"
                  tooltipPosition="bottom"
                  [showDelay]="500">
            <fa-duotone-icon [icon]="faClockRotateLeft"></fa-duotone-icon>
          </button>
          @if (temporal.active()) {
            <!-- A non-textual accent only — the banner (role="status") owns the instant; an aria-label on a
                 plain span names nothing to anyone, so the dot carries none. -->
            <span class="time-travel-active-dot" aria-hidden="true"></span>
          }
        </div>

        }

        <!-- Globalization — always visible (auth + unauth). The content-language state (hidden brand-wide, or
             repositioned because the interface language is outside the offer; the hidden name wins when both
             hold — the rarer, admin-facing state) reaches the member THROUGH THE BUTTON (ruled 2026-09-14): its
             tooltip gains the sentence under the title, a hidden twin describes the button for a screen reader,
             and the dot is a pure accent — an aria-label on a plain span names nothing to anyone. The panel's
             hidden badge (the selected content language's) rides the same sentence on its tooltip; the button
             carries it whenever that badge shows, so the sentence never lives on a hover alone. -->
        @let contentLanguageStateSentenceKey = activeContentLanguageHidden() ? 'contentLanguage.hiddenTooltip' : contentOfferMismatch() ? 'topbar.contentOfferMismatch' : null;
        @let contentLanguageStateSentence = contentLanguageStateSentenceKey
          ? (contentLanguageStateSentenceKey | translate:{ brand: brandDisplayName(), uiLanguage: uiLanguageName(), contentLanguage: contentLanguageName() })
          : null;
        <div class="globe-button-wrapper">
          <button pButton type="button"
                  class="p-button-text p-button-rounded topbar-button"
                  [appTourTarget]="'topbar-globalization'"
                  (click)="globalizationPanel.toggle()"
                  [icon]="'pi pi-false'"
                  [pTooltip]="contentLanguageStateSentence ? ('topbar.languageAndRegion' | translate) + '\n' + contentLanguageStateSentence : ('topbar.languageAndRegion' | translate)"
                  tooltipStyleClass="tooltip-multiline"
                  tooltipPosition="bottom"
                  [showDelay]="500"
                  [attr.aria-describedby]="contentLanguageStateSentence ? 'content-language-state-hint' : null">
            <fa-duotone-icon [icon]="faGlobe"></fa-duotone-icon>
          </button>
          @if (contentLanguageStateSentence) {
            <span class="content-language-state-dot" aria-hidden="true"></span>
            <span class="sr-only" id="content-language-state-hint">{{ contentLanguageStateSentence }}</span>
          }
        </div>

        <!-- Settings (theme) — always visible (auth + unauth) -->
        <button pButton type="button"
                class="p-button-text p-button-rounded topbar-button"
                (click)="settingsPanel.toggle()"
                [icon]="'pi pi-false'"
                [pTooltip]="'topbar.settings' | translate"
                tooltipPosition="bottom"
                [showDelay]="500">
          <fa-duotone-icon [icon]="faGear"></fa-duotone-icon>
        </button>

        <!-- User identity — rightmost position (profile for auth, Sign In for unauth) -->
        @if (isAuthenticated()) {
        <!-- Membership action buttons for authenticated NON-members only: a paused member
             still has a membership row, so join/closed affordances never render for them. -->
        @if (membershipState().loaded && membershipState().standing === 'none') {
        <!-- A monitoring root is authenticated-but-not-a-member, so without the read-only leg
             they would see a live "Join brand" button (the server refuses it, but a lying
             affordance is the defect). Request-access is a navigation — gated anyway for
             coherence. Pending/closed statuses are inert displays and stay. -->
        @if (hasAwaitingInvitation()) {
        <!-- Queued admission outranks every join affordance: the person already said yes —
             offering "Join brand"/"Request access" beside the queue would mint a second,
             contradictory path in. Same disabled-waiting grammar as requestPending. -->
        <button pButton type="button"
                class="topbar-membership-btn pending-btn p-button-outlined"
                [disabled]="true">
          <span>{{ 'topbar.awaitingApproval' | translate }}</span>
        </button>
        } @else if (canJoinDirectly() && !access.isReadOnlyRoot()) {
        <button pButton type="button"
                class="topbar-membership-btn join-btn"
                (click)="joinBrand()">
          <span>{{ 'topbar.joinBrand' | translate }}</span>
        </button>
        } @else if (canRequestToJoin() && !access.isReadOnlyRoot()) {
        <button pButton type="button"
                class="topbar-membership-btn request-btn"
                (click)="requestAccess()">
          <span>{{ 'topbar.requestAccess' | translate }}</span>
        </button>
        } @else if (membershipState().hasPendingRequest) {
        <button pButton type="button"
                class="topbar-membership-btn pending-btn p-button-outlined"
                [disabled]="true">
          <span>{{ 'topbar.requestPending' | translate }}</span>
        </button>
        @if (!access.isReadOnlyRoot()) {
        <!-- The pill is the status; the text button beside it is the door — a pending request the
             member can act on, the same confirm-gated withdraw the Account's My Brands offers. -->
        <button pButton type="button"
                class="topbar-membership-btn withdraw-btn p-button-text"
                [loading]="withdrawing()"
                [disabled]="withdrawing()"
                (click)="cancelJoinRequest()">
          <span>{{ 'topbar.withdrawRequest.action' | translate }}</span>
        </button>
        }
        @if (reconcileFailed()) {
        <!-- A refused withdraw said the request is already gone and the app re-read the truth — and THAT
             re-read failed: the last-known pill stays (nothing truer is known) and says so beside it,
             with the same re-read as its Try again. The request page's card mounts the full ribbon; the
             topbar row carries this compact twin — never silence beside a live Withdraw. -->
        <span class="topbar-membership-stale" role="status">
          {{ 'common.refreshFailed' | translate }}
          <button pButton type="button" class="topbar-membership-btn p-button-text" (click)="retryReconcile()">
            <span>{{ 'common.retry' | translate }}</span>
          </button>
        </span>
        }
        } @else if (isBrandClosed()) {
        <button pButton type="button"
                class="topbar-membership-btn closed-btn p-button-text"
                [disabled]="true">
          <span>{{ 'topbar.invitationRequired' | translate }}</span>
        </button>
        }
        }
        <!-- User Profile -->
        <button pButton type="button"
                class="p-button-text topbar-profile-button"
                [appTourTarget]="'topbar-account'"
                (click)="profilePanel.toggle()"
                [pTooltip]="'topbar.account' | translate"
                tooltipPosition="bottom"
                [showDelay]="500">
          <span class="p-button-label">
            <app-user-avatar [name]="currentUser()?.name" [email]="currentUser()?.email"
                             [imageUrl]="userPicture()" [colorKey]="userColorKey()" size="md"/>
            <span class="profile-name">{{ currentUser()?.name }}</span>
            <fa-duotone-icon [icon]="faChevronDown"></fa-duotone-icon>
          </span>
        </button>
        } @else {
        <!-- Not authenticated — Sign In / Sign Up -->
        <button pButton type="button"
                [label]="'common.signIn' | translate"
                class="p-button-text auth-link"
                (click)="signIn()"></button>
        @if (signupAllowed()) {
        <button pButton type="button"
                [label]="'common.signUp' | translate"
                class="auth-signup-btn"
                (click)="signUp()"></button>
        }
        }
      </div>
    </div>
  </div>
</div>

<!-- Slide Panels -->

<!-- Uploads Panel -->
<app-slide-panel #uploadsPanel [titleIcon]="faCloudArrowUp" [title]="'upload.uploads' | translate">
  <div class="panel-content">
    <app-upload-queue></app-upload-queue>
  </div>
</app-slide-panel>

<!-- Notifications Panel -->
<app-slide-panel #notificationsPanel [titleIcon]="faBell" [title]="'notifications.title' | translate">
  <div panel-actions class="notification-header-actions">
    <!-- Read-marks are brand-scoped member state — a read-only monitoring root has none to
         mark (defense-in-depth over the member-gated bell that opens this panel). -->
    @if (notificationCount() > 0 && !access.isReadOnlyRoot()) {
      <button pButton type="button"
        class="p-button-text p-button-sm notification-action-btn"
        (click)="markAllNotificationsRead()">
        {{ 'notifications.markAllRead' | translate }}
      </button>
    }
    @if (notificationItems().length > 0 && !access.isReadOnlyRoot()) {
      <button pButton type="button"
        class="p-button-text p-button-sm notification-action-btn"
        (click)="dismissAllNotifications()">
        {{ 'notifications.dismissAll' | translate }}
      </button>
    }
    <!-- The annoyance surface and its control meet: a quiet always-visible path to the
         Account app's notification settings (unlike its count-gated siblings). -->
    <button pButton type="button"
      class="p-button-text p-button-sm notification-action-btn"
      (click)="openNotificationSettings()">
      {{ 'notifications.settings' | translate }}
    </button>
  </div>
  <div class="panel-content notification-list">
    @if (notificationsLoading() && notificationItems().length === 0) {
      <!-- Skeleton rows, not a spinner — the rows themselves are what's being awaited. -->
      <div class="notification-skeletons" aria-hidden="true">
        @for (i of [0, 1, 2]; track i) {
          <div class="notification-skeleton-row">
            <p-skeleton width="85%" height="0.875rem" />
            <p-skeleton width="30%" height="0.75rem" />
          </div>
        }
      </div>
    } @else if (notificationsFailed() && notificationItems().length === 0) {
      <!-- A failed read is NOT an empty inbox: "You're all caught up" over a transport
           failure would falsely settle the question. The canonical section-grain face
           owns the failure and offers the retry. -->
      <app-load-failed
        [message]="'notifications.panelLoadFailed' | translate"
        (retry)="retryNotifications()" />
    } @else if (notificationItems().length === 0) {
      <div class="empty-state">
        <fa-duotone-icon [icon]="faBell" class="empty-icon"></fa-duotone-icon>
        <h2>{{ 'notifications.empty' | translate }}</h2>
        <p>{{ 'notifications.emptyHint' | translate }}</p>
      </div>
    } @else {
      @for (notification of notificationItems(); track notification.id) {
        <!-- The POINTER target is the whole row (the inbox grammar of every reference tool:
             padding, dot column and all respond), while the KEYBOARD/assistive control stays the
             inner text block — a row-level key handler would open on the dismiss button's Enter,
             and the dismiss button's own click stopPropagations so its press never opens. -->
        <div class="notification-item" [class.unread]="!notification.isRead"
          (click)="onNotificationClick(notification)">
          <div class="notification-unread-dot">
            @if (!notification.isRead) {
              <span class="dot"></span>
            }
          </div>
          <!-- aria-label REPLACES the block's content for assistive tech, so it must carry both
               members a sighted reader sees: the sentence and the relative time. -->
          <div class="notification-body" role="button" tabindex="0"
            [attr.aria-label]="notification.renderedText + ', ' + notification.relativeTime"
            [attr.aria-describedby]="notification.subjectOriginLocale ? 'notification-origin-' + notification.id : null"
            (keydown.enter)="onNotificationClick(notification)"
            (keydown.space)="onNotificationSpace($event, notification)">
            <p class="notification-text">{{ notification.renderedText }}</p>
            <!-- A workflow subject named through another label language says where the name came from: the
                 chip beside the sentence, nested in the row's Tab stop (its sentence reveals on the row's focus
                 and is the row's description). The bell has no second face, so the sentence row carries it. -->
            @if (notification.subjectOriginLocale; as origin) {
              <app-locale-chip [locale]="origin" [nested]="true" [hintId]="'notification-origin-' + notification.id" />
            }
            <span class="notification-time">{{ notification.relativeTime }}</span>
          </div>
          @if (!access.isReadOnlyRoot()) {
          <button class="notification-dismiss-btn" type="button"
            (click)="dismissNotification($event, notification.id)"
            [attr.aria-label]="'notifications.dismiss' | translate">
            <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
          </button>
          }
        </div>
      }
      @if (notificationsFailed()) {
        <!-- A LATER page failed over rows already on screen: the rows stay and the tail says so with its
             own Try again (the activity film's shape) — never the face over kept rows, never silence
             beside a re-enabled Load more. The page face above is the FIRST read's alone. -->
        <p class="notification-load-more-failed" role="status">
          <span>{{ 'notifications.loadMoreError' | translate }}</span>
          <button pButton type="button" class="p-button-text p-button-sm"
            [disabled]="notificationsLoading()" [loading]="notificationsLoading()"
            (click)="loadMoreNotifications()">
            {{ 'common.retry' | translate }}
          </button>
        </p>
      } @else if (notificationsHasMore()) {
        <!-- The paging machinery existed but the UI never called it — page past 20.
             Insight-rail grammar: visible while more exists, disabled while a page loads. -->
        <button pButton type="button" class="p-button-text p-button-sm notification-load-more"
          [disabled]="notificationsLoading()"
          (click)="loadMoreNotifications()">
          {{ 'common.loadMore' | translate }}
        </button>
      }
    }
  </div>
</app-slide-panel>

<!-- Bookmarks Panel -->
<app-slide-panel #bookmarksPanel [titleIcon]="faBookmark" [title]="'bookmarks.title' | translate">
  <div class="panel-content bookmark-list">
    @if (bookmarksLoading() && bookmarks().length === 0) {
      <div class="bookmark-loading">
        <span class="spinner"></span>
      </div>
    } @else if (bookmarksFailed() && bookmarks().length === 0) {
      <!-- A failed read is NOT an empty list: "Your bookmarks live here" over a transport
           failure would falsely settle the question. The canonical section-grain face
           owns the failure and offers the retry (the bell panel's shape). -->
      <app-load-failed
        [message]="'bookmarks.panelLoadFailed' | translate"
        (retry)="retryBookmarks()" />
    } @else if (bookmarks().length === 0) {
      <div class="empty-state">
        <fa-duotone-icon [icon]="faBookmark" class="empty-icon"></fa-duotone-icon>
        <h2>{{ 'bookmarks.empty' | translate }}</h2>
        <p>{{ 'bookmarks.emptyHint' | translate }}</p>
      </div>
    } @else {
      @for (bookmark of bookmarks(); track bookmark.item.id) {
        <div class="bookmark-item"
          (click)="navigateToBookmark(bookmark)">
          <div class="bookmark-thumbnail">
            @if (bookmarkService.getThumbnailUrl(bookmark); as thumbUrl) {
              <img [src]="thumbUrl" [alt]="bookmarkTitle(bookmark)" loading="lazy" />
            } @else {
              <fa-duotone-icon [icon]="bookmarkService.getItemIcon(bookmark)"></fa-duotone-icon>
            }
          </div>
          <!-- The KEYBOARD/assistive control is the inner body (the notification row's grammar): a
               row-level key handler would open on the dismiss button's Enter. The aria-label
               replaces the block's content, so it carries the title and its module. -->
          <div class="bookmark-body" role="button" tabindex="0"
            [attr.aria-label]="bookmarkTitle(bookmark) + ', ' + ((bookmarkService.getModuleLabelKey(bookmark.path) ?? '') | translate)"
            (keydown.enter)="navigateToBookmark(bookmark)"
            (keydown.space)="onBookmarkSpace($event, bookmark)">
            <p class="bookmark-title">{{ bookmarkTitle(bookmark) }}</p>
            <span class="bookmark-meta">{{ (bookmarkService.getModuleLabelKey(bookmark.path) ?? '') | translate }} · {{ bookmarkService.itemTypeLabelKey(bookmark.item.typeName) | translate }}</span>
          </div>
          <span class="bookmark-time">{{ bookmarkService.getRelativeTime(bookmark.bookmarkedAt) }}</span>
          <!-- Remove = a write; bookmark NAVIGATION stays and exits "Time travel" mode -->
          @if (!temporal.active()) {
            <button class="bookmark-dismiss-btn"
              (click)="removeBookmark($event, bookmark.item.id)"
              [attr.aria-label]="'bookmarks.removeBookmark' | translate">
              <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
            </button>
          }
        </div>
      }
    }
  </div>
</app-slide-panel>

<!-- Globalization Panel -->
<app-slide-panel #globalizationPanel [titleIcon]="faGlobe" [title]="'topbar.languageAndRegion' | translate"
                 [closeOnClickOutside]="tourRevealKey() !== 'globalization'">
  <div class="panel-content">
    <div class="field">
      <label>{{ 'topbar.uiLanguage' | translate }}</label>
      <p-select
        [options]="uiLanguages"
        [ngModel]="selectedUILanguage()"
        (ngModelChange)="selectedUILanguage.set($event)"
        (onChange)="onUILanguageChange()"
        [style]="{'width': '100%'}"
        appendTo="body">
      </p-select>
    </div>
    <div class="field">
      <label>{{ 'topbar.contentLanguage' | translate }}</label>
      <p-select
        [options]="contentLanguages()"
        [ngModel]="selectedContentLanguage()"
        (ngModelChange)="selectedContentLanguage.set($event)"
        (onChange)="onContentLanguageChange()"
        [style]="{'width': '100%'}"
        appendTo="body">
        <ng-template pTemplate="selectedItem" let-l>
          <span class="cl-option">{{ l.label }}
            @if (isLocaleHidden(l.value)) {
              <span class="locale-badge hidden-badge" [pTooltip]="'contentLanguage.hiddenTooltip' | translate">
                <fa-duotone-icon [icon]="faEyeSlash"></fa-duotone-icon> {{ 'settings.languages.hidden' | translate }}
              </span>
            }
          </span>
        </ng-template>
        <ng-template pTemplate="item" let-l>
          <span class="cl-option">{{ l.label }}
            @if (isLocaleHidden(l.value)) {
              <span class="locale-badge hidden-badge">
                <fa-duotone-icon [icon]="faEyeSlash"></fa-duotone-icon> {{ 'settings.languages.hidden' | translate }}
              </span>
            }
          </span>
        </ng-template>
      </p-select>
      @if (contentOfferMismatch()) {
        <!-- The interface language is outside the brand's content offer: the content language was
             repositioned to the brand's first language, and the field says so where it moved. -->
        <p class="field-hint offer-mismatch">{{ 'topbar.contentOfferMismatch' | translate:{ brand: brandDisplayName(), uiLanguage: uiLanguageName(), contentLanguage: contentLanguageName() } }}</p>
      }
    </div>
    <p-divider></p-divider>
    <h4>{{ 'topbar.region' | translate }}</h4>
    <div class="field">
      <label>{{ 'topbar.timeZone' | translate }}</label>
      <p-select
        [options]="timezones"
        [ngModel]="selectedTimezone()"
        (ngModelChange)="selectedTimezone.set($event)"
        (onChange)="onTimezoneChange()"
        [style]="{'width': '100%'}"
        appendTo="body">
      </p-select>
    </div>
  </div>
</app-slide-panel>

<!-- Settings Panel -->
<app-slide-panel #settingsPanel [titleIcon]="faGear" [title]="'topbar.settings' | translate">
  <div class="panel-content">
    <h4>{{ 'topbar.theme' | translate }}</h4>
    <!-- Real buttons (the Account panel's shape): focusable, Enter/Space activate, and
         aria-pressed names the current theme for assistive tech. -->
    <div class="theme-selector">
      <button pButton type="button" class="theme-option"
        [class.active]="currentTheme() === 'light'"
        [attr.aria-pressed]="currentTheme() === 'light'"
        (click)="setTheme('light')">
        <div class="theme-preview theme-light">
          <div class="preview-header"></div>
          <div class="preview-content"></div>
        </div>
        <span>{{ 'topbar.light' | translate }}</span>
      </button>
      <button pButton type="button" class="theme-option"
        [class.active]="currentTheme() === 'dark'"
        [attr.aria-pressed]="currentTheme() === 'dark'"
        (click)="setTheme('dark')">
        <div class="theme-preview theme-dark">
          <div class="preview-header"></div>
          <div class="preview-content"></div>
        </div>
        <span>{{ 'topbar.dark' | translate }}</span>
      </button>
      <button pButton type="button" class="theme-option"
        [class.active]="currentTheme() === 'auto'"
        [attr.aria-pressed]="currentTheme() === 'auto'"
        (click)="setTheme('auto')">
        <div class="theme-preview theme-auto">
          <div class="preview-header"></div>
          <div class="preview-content"></div>
        </div>
        <span>{{ 'topbar.auto' | translate }}</span>
      </button>
    </div>
  </div>
</app-slide-panel>

<!-- Time travel Panel — lg width; content component owns the five-state chain -->
<app-slide-panel #timeTravelPanel [titleIcon]="faClockRotateLeft" [title]="'temporal.panel.title' | translate" width="lg">
  <div class="panel-content">
    <app-temporal-panel #timeTravelContent (entered)="timeTravelPanel.close()"></app-temporal-panel>
  </div>
</app-slide-panel>

<!-- Feedback Panel — guarded while its draft is dirty: every member dismiss (X, Escape, outside
     click, toggle-off) and a gesture eviction ask first; the content component owns the prompt. -->
<app-slide-panel #feedbackPanel [titleIcon]="faCommentDots" [title]="'feedback.title' | translate"
                 [closeOnClickOutside]="tourRevealKey() !== 'feedback'"
                 [dismissGuarded]="feedbackDirty()"
                 (dismissRequested)="onFeedbackDismissRequested()">
  <div class="panel-content">
    <app-feedback-panel></app-feedback-panel>
  </div>
</app-slide-panel>

<!-- Profile Panel -->
<app-slide-panel #profilePanel [titleIcon]="faCircleUser" [title]="'topbar.account' | translate"
                 [closeOnClickOutside]="tourRevealKey() !== 'account'">
  <div class="panel-content">
    <!-- User Info Section -->
    <div class="profile-info">
      <app-user-avatar [name]="currentUser()?.name" [email]="currentUser()?.email"
                       [imageUrl]="userPicture()" [colorKey]="userColorKey()" size="lg" class="mb-3"/>
      <h4>{{ currentUser()?.name }}</h4>
      <p class="text-secondary">{{ currentUser()?.email }}</p>
      @if (isAdmin()) {
        <p class="text-secondary">
          <fa-duotone-icon [icon]="faShieldHalved" class="mr-1"></fa-duotone-icon>
          {{ 'topbar.brandAdmin' | translate }}
        </p>
      }
    </div>

    <!-- Quick Actions — grouped with section dividers -->
    <div class="profile-menu">
      <!-- My Account -->
      <button pButton type="button"
        class="p-button-text profile-menu-item w-full"
        (click)="navigateToAccount()">
        <span class="profile-menu-item-content">
          <fa-duotone-icon [icon]="faUser" class="menu-icon"></fa-duotone-icon>
          <span class="menu-label">{{ 'topbar.myAccount' | translate }}</span>
        </span>
      </button>

      <!-- My Activity -->
      <button pButton type="button"
        class="p-button-text profile-menu-item w-full"
        (click)="navigateToActivity()">
        <span class="profile-menu-item-content">
          <fa-duotone-icon [icon]="faClockRotateLeft" class="menu-icon"></fa-duotone-icon>
          <span class="menu-label">{{ 'topbar.myActivity' | translate }}</span>
        </span>
      </button>

      <!-- Shares I sent -->
      <button pButton type="button"
        class="p-button-text profile-menu-item w-full"
        (click)="navigateToSharesSent()">
        <span class="profile-menu-item-content">
          <fa-duotone-icon [icon]="faPaperPlane" class="menu-icon"></fa-duotone-icon>
          <span class="menu-label">{{ 'sharesSent.title' | translate }}</span>
        </span>
      </button>

      <div class="profile-menu-divider"></div>

      <!-- Keyboard shortcuts — a read-only utility beside Help & support, not a personal-area
           door like the entries above. The ? key and this item reach the SAME population (every
           signed-in person, demo included); the surfaces the listed keys act on still gate
           themselves. -->
      <button pButton type="button"
        class="p-button-text profile-menu-item w-full"
        (click)="openKeyboardShortcuts()">
        <span class="profile-menu-item-content">
          <fa-duotone-icon [icon]="faKeyboard" class="menu-icon"></fa-duotone-icon>
          <span class="menu-label">{{ 'topbar.keyboardShortcuts' | translate }}</span>
        </span>
      </button>

      <!-- Help & Support — opens the help hub on the marketing site in a new tab (one destination
           for both apps; the address lives in the environment beside the other site URLs) -->
      <a pButton [href]="helpUrl" target="_blank" rel="noopener"
        class="p-button-text profile-menu-item w-full">
        <span class="profile-menu-item-content">
          <fa-duotone-icon [icon]="faLifeRing" class="menu-icon"></fa-duotone-icon>
          <span class="menu-label">{{ 'topbar.helpAndSupport' | translate }}</span>
        </span>
      </a>

      <!-- Sign Out -->
      <button pButton type="button"
        class="p-button-text profile-menu-item w-full"
        (click)="signOut()">
        <span class="profile-menu-item-content">
          <fa-duotone-icon [icon]="faRightFromBracket" class="menu-icon"></fa-duotone-icon>
          <span class="menu-label">{{ 'common.signOut' | translate }}</span>
        </span>
      </button>
    </div>
  </div>
</app-slide-panel>
